import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/firebase';

export type LearningRestrictionSettings = {
  quizEnabled: boolean;
  mockExamEnabled: boolean;
  viewTextEnabled: boolean;
  examEnabled: boolean;
  message: string;
};

export const DEFAULT_LEARNING_RESTRICTIONS: LearningRestrictionSettings = {
  quizEnabled: true,
  mockExamEnabled: true,
  viewTextEnabled: true,
  examEnabled: true,
  message: '現在システム調整中のためご利用いただけません。しばらく時間をおいて再度お試しください。',
};

export const FAILSAFE_LEARNING_RESTRICTIONS: LearningRestrictionSettings = {
  quizEnabled: false,
  mockExamEnabled: false,
  viewTextEnabled: false,
  examEnabled: false,
  message:
    '現在システム調整中のためご利用いただけません。時間をおいて再度お試しください。',
};

const restrictionsDocRef = () => doc(db, 'SystemSettings', 'learningRestrictions');

const normalizeLearningRestrictions = (
  data?: Partial<LearningRestrictionSettings>
): LearningRestrictionSettings => ({
  ...DEFAULT_LEARNING_RESTRICTIONS,
  quizEnabled: data?.quizEnabled ?? DEFAULT_LEARNING_RESTRICTIONS.quizEnabled,
  mockExamEnabled: data?.mockExamEnabled ?? DEFAULT_LEARNING_RESTRICTIONS.mockExamEnabled,
  viewTextEnabled: data?.viewTextEnabled ?? DEFAULT_LEARNING_RESTRICTIONS.viewTextEnabled,
  examEnabled: data?.examEnabled ?? DEFAULT_LEARNING_RESTRICTIONS.examEnabled,
  message: data?.message ?? DEFAULT_LEARNING_RESTRICTIONS.message,
});

export const getLearningRestrictions = async (): Promise<LearningRestrictionSettings> => {
  const snapshot = await getDoc(restrictionsDocRef());
  if (!snapshot.exists()) {
    return DEFAULT_LEARNING_RESTRICTIONS;
  }

  return normalizeLearningRestrictions(snapshot.data() as Partial<LearningRestrictionSettings>);
};

export const subscribeLearningRestrictions = (
  onChange: (settings: LearningRestrictionSettings) => void,
  onError?: (error: Error) => void
): (() => void) =>
  onSnapshot(
    restrictionsDocRef(),
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange(DEFAULT_LEARNING_RESTRICTIONS);
        return;
      }

      onChange(normalizeLearningRestrictions(snapshot.data() as Partial<LearningRestrictionSettings>));
    },
    (error) => {
      console.error('Error subscribing learning restrictions:', error);
      onError?.(error);
    }
  );

export const saveLearningRestrictions = async (
  settings: LearningRestrictionSettings
): Promise<void> => {
  await setDoc(restrictionsDocRef(), settings, { merge: true });
};
